import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { SupabaseService } from './base/supabase.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  public supabaseClient: SupabaseClient;
  private sbs: SupabaseService = inject(SupabaseService);

  constructor() {
    this.supabaseClient = this.sbs.supabase;
  }

  /**
   *
   * @param bucket buckName
   * @param path  walks with filename
   * @param file file
   * @returns
   */
  async upload(bucket: string, path: string, file: File, waittime: number = 13000) {
    // Upload to quarantine bucket first
    const quarantineBucket = 'quarantine';
    const { data: uploadData, error: uploadError } =
      await this.supabaseClient.storage
        .from(quarantineBucket)
        .upload(bucket + '/' + path, file, {  contentType: file.type});

    if (uploadError) {
      return { data: null, error: uploadError };
    }

    try {
      // Get the session to obtain the access token
      const { data: { session } } = await this.supabaseClient.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('No active session found');
      }

      // Trigger Lambda for antivirus scanning
      const response = await fetch(`${environment.lambdaUrl}/enqueue_for_av`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quarantineBucket,
          quarantineFilePath: bucket+'/'+path,
          definitiveBucket: bucket,
          definitiveFilePath: path
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to trigger AV scan: ${response.statusText}`);
      }

      // Wait for 13 seconds before returning
      await new Promise(resolve => setTimeout(resolve, waittime));

      return { data: uploadData, error: null };
    } catch (error) {
      console.error('Error during upload and scan process:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   *
   * @param bucket  buckName
   * @param path walks with filename
   */
  async download(bucket: string, path: string) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucket)
      .download(path);

    return { data, error };
  }

  /**
   *
   * @param newBucket  buckName
   * @returns bucket creation data
   */
  async createBucket(newBucket: string) {
    const { data, error } = await this.supabaseClient.storage.createBucket(
      newBucket
    );
    return { data, error };
  }

  /**
   *
   * @param bucket buckName
   * @param folder walks with filename
   * @returns json with signed url
   */
  async signedUrl(bucket: string, folder: string) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucket)
      .createSignedUrl(folder, 60);

    return { data, error };
  }

  async getBucket(nameBucket: string) {
    const { data, error } = await this.supabaseClient.storage.getBucket(
      nameBucket
    );
    return { data, error };
  }

  async getAllBucket() {
    const { data, error } = await this.supabaseClient.storage.listBuckets();

    return { data, error };
  }

  async deleteBucket(bucketName: string) {
    const { data, error } = await this.supabaseClient.storage.deleteBucket(
      bucketName
    );
    return { data, error };
  }

  async emptyBucket(bucketName: string) {
    const { data, error } = await this.supabaseClient.storage.emptyBucket(
      bucketName
    );
    return { data, error };
  }

  async listAllFilesBucket(bucketName: string, folder: string) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucketName)
      .list(folder, {
        limit: 100,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' },
      });
    return { data, error };
  }

  async replaceExistingFile(bucketName: string, path: string, file: File) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucketName)
      .update(path, file, {
        cacheControl: '3600',
        // Overwrite file if it exis
        upsert: true,
      });

    return { data, error };
  }

  async DeleteFilesInBucket(bucketName: string, path: string[]) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucketName)
      .remove(path);

    return { data, error };
  }

  async CreateMultipleSignedUrls(bucket: string, paths: string[]) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucket)
      .createSignedUrls(paths, 60);
    return { data, error };
  }
}
